import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AiInterviewSessionService } from '@edtech-ui/core/services/ai-interview-session-service/ai-interview-session.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-step-device-check',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './step-device-check.component.html',
  styleUrl: './step-device-check.component.scss'
})
export class StepDeviceCheckComponent {
  @Output() continue = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('micBar', { static: false }) micBar!: ElementRef<HTMLDivElement>;

  constructor(private toast: ToastrService, private session: AiInterviewSessionService) {

  }

  cameraReady = false;
  micReady = false;
  stream: MediaStream | null = null;
  audioContext?: AudioContext;
  analyser?: AnalyserNode;
  micSource?: MediaStreamAudioSourceNode;
  dataArray?: Uint8Array;
  animationFrameId?: number;
  micLevel = 0;
  errorMessage = '';
  micStream: MediaStream | null = null; 


  async testCamera() {
    try {
      this.stopAll(); 
      this.stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = this.videoElement.nativeElement;
      video.srcObject = this.stream;
      video.play();
      this.cameraReady = true;
      this.errorMessage = '';
    } catch (error) {
      this.cameraReady = false;
      this.errorMessage = 'Unable to access camera. Please check permissions.';
    }
  }

  async testMicrophone() {
    try {
      if (this.audioContext) this.audioContext.close();

      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.micSource = this.audioContext.createMediaStreamSource(micStream);
      this.micSource.connect(this.analyser);

      this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
      this.micReady = true;
      this.animateMic();
      this.errorMessage = '';
    } catch (error) {
      this.micReady = false;
      this.errorMessage = 'Unable to access microphone. Please check permissions.';
    }
  }
  animateMic() {
    if (!this.analyser || !this.dataArray) return;

    const analyser = this.analyser;
    const dataArray = this.dataArray;
    const micBar = this.micBar.nativeElement;

    const update = () => {
      analyser.getByteFrequencyData(dataArray);

      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      const level = Math.min(100, Math.round((avg / 255) * 100));

      micBar.style.width = `${level}%`;
      this.micLevel = level;

      this.animationFrameId = requestAnimationFrame(update);
    };

    update();
  }


  refreshAll() {
    this.stopAll();
    this.cameraReady = false;
    this.micReady = false;
    this.micLevel = 0;
    this.errorMessage = '';
  }

  stopAll() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    if (this.audioContext) this.audioContext.close();
  }

  ngOnDestroy() {
    this.stopAll();
  }

  get allSystemsReady() {
    return this.cameraReady && this.micReady;
  }

onContinue() {
  if (!this.cameraReady || !this.micReady) {
    this.toast.warning('Please test both camera and mic before continuing.');
    return;
  }

  this.session.update({
    microphone_status: true,
    camera_status: true,
    internet_status: true,
    internet_speed_mbps: 50
  });

  this.continue.emit();
}

ngOnInit() {
  const saved = this.session.value;
  if (saved.microphone_status) this.micReady = saved.microphone_status;
  if (saved.camera_status) this.cameraReady = saved.camera_status;
}


}
